import React, { useState, useEffect, useRef, useMemo } from "react";
import "@fullcalendar/daygrid/main.css";
import moment from "moment";
import { Button, CircularProgress } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useSnackbar } from "notistack";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import GeneralCalendar from "src/components/GeneralComponents/GeneralCalendar";
import { programme_general_workout_list_api } from "src/DAL/programmeWorkout/programmeWorkout";
import { memberDetail, memberRewardDetail } from "src/DAL/Profile/Profile";
import CustomPopoverNew from "src/components/CustomPopoverNew";

const useStyles = makeStyles(() => ({
  loading: {
    marginLeft: "50%",
    marginTop: "20%",
  },
}));

const get_view_dates = (calendarValue) => {
  let tool = calendarValue;

  const startOfMonth = moment().startOf(tool);
  const daysToSubtract = startOfMonth.day() - 0;
  const previousSunday = startOfMonth.subtract(daysToSubtract, "days");
  let previousSundayDate = previousSunday.toDate();
  let add_days = 42;
  if (tool == "week") {
    add_days = 6;
  } else if (tool == "day") {
    add_days = 0;
  }
  let nextSaturday = startOfMonth.add(add_days, "days");
  let nextSaturdayDate = nextSaturday.toDate();
  if (tool == "day") {
    previousSundayDate = new Date();
    nextSaturdayDate = new Date();
  }

  return {
    start_date: previousSundayDate,
    end_date: nextSaturdayDate,
  };
};

function Calender() {
  const [openLevels, setOpenLevels] = useState(false);
  const calendarRef = useRef(null);

  const navigate = useNavigate();
  const { state } = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();
  const [currentDate, setCurrentDate] = useState(get_view_dates("month"));

  const startDateStr = useMemo(
    () => moment(currentDate.start_date).format("YYYY-MM-DD"),
    [currentDate.start_date],
  );

  const endDateStr = useMemo(
    () => moment(currentDate.end_date).format("YYYY-MM-DD"),
    [currentDate.end_date],
  );
  const goToToday = () => {
    if (calendarRef?.current) {
      const calendarApi = calendarRef.current.getApi();
      const today = new Date();
      const viewStart = calendarApi.view.currentStart;
      const viewMonth = viewStart.getMonth();
      const viewYear = viewStart.getFullYear();

      const todayMonth = today.getMonth();
      const todayYear = today.getFullYear();

      const isSameMonth = viewMonth === todayMonth && viewYear === todayYear;

      if (!isSameMonth) {
        calendarApi.gotoDate(today);
        const newDateRange = {
          start_date: calendarApi.view.activeStart,
          end_date: calendarApi.view.activeEnd,
        };
        setCurrentDate(newDateRange);
      } else {
        const todayDate = today.toISOString().slice(0, 10);
        const todayCell = document.querySelector(
          `.fc-day[data-date="${todayDate}"]`,
        );
        if (todayCell) {
          todayCell.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
    }
  };

  const { data: memberData = {} } = useQuery({
    queryKey: ["memberDetail"],
    queryFn: async () => {
      const result = await memberDetail();
      if (result?.code !== 200) {
        throw new Error(result?.message);
      }
      return result.member;
    },
    onError: (error) => {
      enqueueSnackbar(error?.message, { variant: "error" });
    },
  });

  const { data: memberRewardData = {} } = useQuery({
    queryKey: ["memberRewardDetail"],
    queryFn: async () => {
      const result = await memberRewardDetail();
      if (result?.code !== 200) {
        throw new Error(result?.message);
      }
      return result?.reward_points;
    },
    onError: (error) => {
      enqueueSnackbar(error?.message, { variant: "error" });
    },
  });

  const workoutListQuery = useQuery({
    queryKey: ["programmeGeneralWorkoutList", startDateStr, endDateStr],
    keepPreviousData: true,
    queryFn: async () => {
      const postData = {
        start_date: startDateStr,
        end_date: endDateStr,
        slug: "",
      };
      const result = await programme_general_workout_list_api(postData);
      if (result?.code !== 200) {
        throw new Error(result?.message);
      }

      const mergedData = [
        ...result?.workouts,
        ...result?.program_workouts.filter(
          (item) =>
            !result?.workouts.find(
              (existingItem) => existingItem.workout_id === item._id,
            ),
        ),
      ];

      const processedData = mergedData.map((workout) => ({
        ...workout,
        exercises: workout.exercises?.map((exercise) => ({
          ...exercise,
          sets: exercise.sets?.map((set) => ({
            ...set,
            vdot: null,
            pace_low: 0,
            pace_up: 423,
          })),
          sub_exercises: exercise.sub_exercises?.map((subExercise) => ({
            ...subExercise,
            sets: subExercise.sets?.map((set) => ({
              ...set,
              vdot: null,
              pace_low: 0,
              pace_up: 423,
            })),
          })),
        })),
      }));

      return {
        workouts: processedData,
        reordered_workouts: result?.reordered_workouts || [],
        difficultyPrograms: result?.disabledDifficultyPrograms || [],
        timeZone: "asia/karachi",
      };
    },
    onSuccess: () => {
      const today = new Date();
      const todayDate = today.toISOString().slice(0, 10);
      const todayCell = document.querySelector(
        `.fc-day[data-date="${todayDate}"]`,
      );
      if (todayCell) {
        todayCell.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    },
    onError: (error) => {
      enqueueSnackbar(error?.message, { variant: "error" });
    },
  });

  const isLoading = workoutListQuery?.isLoading && !workoutListQuery?.data;
  const manualLoading =
    workoutListQuery?.isFetching && !workoutListQuery?.isLoading;

  const workouts = useMemo(
    () => workoutListQuery.data?.workouts || [],
    [workoutListQuery.data?.workouts],
  );

  const reorderWorkouts = useMemo(
    () => workoutListQuery.data?.reordered_workouts || [],
    [workoutListQuery.data?.reordered_workouts],
  );

  const difficultyProgram = useMemo(
    () => workoutListQuery.data?.difficultyPrograms || [],
    [workoutListQuery.data?.difficultyPrograms],
  );

  const defaultTimeZone = workoutListQuery.data?.timeZone || "asia/karachi";

  useEffect(() => {
    if (difficultyProgram.length > 0) {
      setOpenLevels(true);
    }
  }, [difficultyProgram]);

  if (isLoading) {
    return <CircularProgress className={classes.loading} color="primary" />;
  }

  return (
    <>
      <GeneralCalendar
        programWorkoutList={workouts}
        reorderedWorkoutList={reorderWorkouts}
        setCurrentDate={setCurrentDate}
        showAddEvent={true}
        time_zone={defaultTimeZone}
        currentDate={currentDate}
        isLoading={isLoading}
        reloadList={workoutListQuery.refetch}
        calendar_title="Calendar"
        programme={state}
        member_vdot={
          memberData?.pace_calculator ? memberData?.vdot?.vdot_value : null
        }
        memberRewardData={memberRewardData}
        calendarRef={calendarRef}
        goToToday={goToToday}
        manualLoading={manualLoading}
      />

      <CustomPopoverNew
        is_hide_icon={true}
        width="550px"
        isOpenPop={openLevels}
        isClosePop={() => {}}
        title={""}
        componentToPassDown={
          <div className="px-4 pb-3">
            <div
              className="mb-2"
              style={{
                width: "500px",
                maxWidth: "100%",
              }}
            >
              Difficulty level is not set for the following programs, please
              naviagte to Programmes tab and set the difficulty level:
              {difficultyProgram
                .map((program) => program?.programme_title)
                .join("; ")}
              {"; "}
            </div>
            <div className="text-end">
              <Button
                variant="contained"
                onClick={() => navigate(`/programmes`)}
              >
                Go to Programmes
              </Button>
            </div>
          </div>
        }
      />
    </>
  );
}

export default Calender;
